import { Map as MapboxMap } from 'mapbox-gl'

import { IGroundRaster } from './labelGroundTone'
import { IRasterRuntime } from './rasterGroundReader'
import {
  describeRasterLayers,
  ILayerSnapshot,
  ISourceSnapshot,
} from './rasterGroundSources'
import { averageRgba, averageRgbaRect } from './rasterSampleAverage'
import { IPixelRect, scaleRectToDecodedSize } from './rasterTileMath'

// The only file here that touches mapbox, the DOM or the network, and deliberately the only one
// with no decisions in it. It reads SOURCE assets — a tile as served, or an ImageSource's own
// bitmap — never the rendered canvas, which is what makes it immune to the self-reference that
// broke the original sampler.

// Mapbox 3.x fetches every raster tile and image itself, so anything visible on our map has
// already satisfied CORS for our origin — and a bitmap decoded from a blob cannot taint a
// canvas. Readability therefore follows from visibility; there is nothing to negotiate.

// Shares the connection budget with mapbox's own tile loading, which is the traffic that
// actually matters while the camera is settling.
// Long enough that a slow-but-alive tile host still answers, short enough that four dead
// requests cannot hold the queue shut for a session.
const TILE_READ_TIMEOUT_MS = 15_000

const MAX_CONCURRENT_READS = 4

// Mapbox keeps TileJSON bounds as a LngLatBounds inside TileBounds, null when none are declared.
interface IMapboxTileBounds {
  bounds?: {
    getWest(): number
    getSouth(): number
    getEast(): number
    getNorth(): number
  } | null
}

function boundsOf(
  tileBounds?: IMapboxTileBounds | null,
  declared?: [number, number, number, number],
): [number, number, number, number] | undefined {
  const b = tileBounds?.bounds
  if (b) return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
  // Declared tiles carry their bounds in options from the start, as `tiles` itself does.
  return declared
}

// An image source is read back ONCE and kept as pixels: getImageData is the expensive part and
// does not get cheaper for being small, while sampling an array afterwards costs microseconds.
// Capped rather than full size — a full sheet is megabytes as RGBA and this ships in a WebView
// with an OOM history, and one cell already spans many source pixels.
const MAX_IMAGE_SAMPLE_EDGE = 1024

// How long a style enumeration stays good. getStyle serialises every layer and source —
// 4.5 ms on a 67-layer style — and a settle asks for it twice per label layer, so five
// layers cost ten of them. The style itself changes on styledata, which invalidates this.
const STYLE_SNAPSHOT_TTL_MS = 250

interface IImageSample {
  source: CanvasImageSource
  width: number
  height: number
  scale: number
  data: Uint8ClampedArray
}

let scratch: OffscreenCanvas | null = null
let scratchCtx: OffscreenCanvasRenderingContext2D | null = null

function getScratch(
  width: number,
  height: number,
): OffscreenCanvasRenderingContext2D | null {
  if (!scratch) {
    scratch = new OffscreenCanvas(Math.max(1, width), Math.max(1, height))
    scratchCtx = scratch.getContext('2d', { willReadFrequently: true })
  }
  if (!scratchCtx) return null
  if (scratch.width < width) scratch.width = width
  if (scratch.height < height) scratch.height = height
  return scratchCtx
}

// The rect a cell covers grows without bound as the camera pulls back, and reading it back
// pixel for pixel is the difference between a settle costing milliseconds and costing
// seconds. The rect is drawn DOWN into a small target instead, so the readback is a fixed
// cost whatever ground the cell spans. The driver's box filter works in gamma space rather
// than linear, which is a small bias on a mean we only use to choose between two inks.
const SAMPLE_TARGET_EDGE = 32

function sampleFrom(
  source: CanvasImageSource,
  rect: IPixelRect,
): IGroundRaster | null {
  const w = Math.max(1, Math.min(SAMPLE_TARGET_EDGE, rect.width))
  const h = Math.max(1, Math.min(SAMPLE_TARGET_EDGE, rect.height))
  const ctx = getScratch(w, h)
  if (!ctx) return null
  ctx.clearRect(0, 0, w, h)
  ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, w, h)
  return averageRgba(ctx.getImageData(0, 0, w, h).data)
}

// One readback per image source, downscaled if the sheet is large. Everything after this is
// array arithmetic.
function readBackImage(
  image: CanvasImageSource,
  width: number,
  height: number,
): IImageSample | null {
  const scale = Math.min(1, MAX_IMAGE_SAMPLE_EDGE / Math.max(width, height))
  const w = Math.max(1, Math.round(width * scale))
  const h = Math.max(1, Math.round(height * scale))
  const ctx = getScratch(w, h)
  if (!ctx) return null
  ctx.clearRect(0, 0, w, h)
  ctx.drawImage(image, 0, 0, width, height, 0, 0, w, h)
  return {
    source: image,
    width: w,
    height: h,
    scale,
    data: ctx.getImageData(0, 0, w, h).data,
  }
}

function canReadPixels(): boolean {
  return (
    typeof createImageBitmap === 'function' &&
    typeof OffscreenCanvas === 'function'
  )
}

export function createMapboxRasterRuntime(
  map: MapboxMap,
): IRasterRuntime | null {
  if (!canReadPixels()) return null

  let inFlight = 0
  const waiting: Array<() => void> = []
  const imageSamples = new Map<string, IImageSample | null>()
  let styleSnapshot: {
    at: number
    layers: ILayerSnapshot[]
    sources: Record<string, ISourceSnapshot | undefined>
  } | null = null

  map.on('styledata', () => {
    styleSnapshot = null
    imageSamples.clear()
  })

  // Reads QUEUE rather than drop: the reader memoises whatever comes back, so a dropped read
  // would persist as "transparent ground" for the session. release HANDS the slot to a waiter
  // rather than freeing it — decrement-then-wake leaves the slot open for a microtask, and an
  // acquire in that window takes it while the waiter increments on top.
  const acquire = async (): Promise<void> => {
    if (inFlight < MAX_CONCURRENT_READS) {
      inFlight++
      return
    }
    await new Promise<void>(resolve => waiting.push(resolve))
  }

  const release = (): void => {
    const next = waiting.shift()
    if (next) {
      next()
      return
    }
    inFlight--
  }

  // The reader reports an unreadable or disabled source through this seam, and the tests
  // assert on what arrives. Production says nothing: every one of these is a recoverable
  // state the reader already handles by falling back to the basemap, so printing it would
  // only be noise on a loop for a project whose overlays genuinely differ.
  const report = (): void => undefined

  return {
    describeLayers(mapZoom) {
      // The zoom filtering below is cheap and has to stay current; the serialisation does
      // not, so only that is cached.
      const fresh =
        styleSnapshot &&
        performance.now() - styleSnapshot.at < STYLE_SNAPSHOT_TTL_MS
      if (fresh && styleSnapshot) {
        return describeRasterLayers(
          styleSnapshot.layers,
          styleSnapshot.sources,
          mapZoom,
        )
      }
      let layers: ILayerSnapshot[] = []
      const sources: Record<string, ISourceSnapshot | undefined> = {}
      try {
        // Throws while the style is mid-load; an empty answer degrades to the basemap tone.
        const style = map.getStyle()
        layers = (style?.layers ?? []).map(l => {
          const raw = l as unknown as {
            id: string
            type: string
            source?: string
            minzoom?: number
            maxzoom?: number
            layout?: { visibility?: string }
            paint?: { 'raster-opacity'?: number }
          }
          return {
            id: raw.id,
            type: raw.type,
            source: raw.source,
            minzoom: raw.minzoom,
            maxzoom: raw.maxzoom,
            visibility: raw.layout?.visibility,
            rasterOpacity:
              typeof raw.paint?.['raster-opacity'] === 'number'
                ? raw.paint['raster-opacity']
                : undefined,
          }
        })
        for (const layer of layers) {
          if (layer.type !== 'raster' || !layer.source) continue
          if (sources[layer.source]) continue
          const s = map.getSource(layer.source) as unknown as {
            type?: string
            tiles?: string[]
            _options?: {
              tiles?: string[]
              bounds?: [number, number, number, number]
            }
            tileSize?: number
            scheme?: string
            minzoom?: number
            maxzoom?: number
            tileBounds?: IMapboxTileBounds | null
            image?: { width?: number; height?: number }
            coordinates?: Array<[number, number]>
          }
          if (!s) continue
          sources[layer.source] = {
            type: s.type ?? 'unknown',
            // Mapbox moves the templates onto the source only once its TileJSON resolves;
            // a source declared with explicit tiles carries them in its options all along.
            tiles: s.tiles ?? s._options?.tiles,
            tileSize: s.tileSize,
            scheme: s.scheme,
            minzoom: s.minzoom,
            maxzoom: s.maxzoom,
            // Flattened so the pure half never holds a mapbox object.
            bounds: boundsOf(s.tileBounds, s._options?.bounds),
            imageWidth: s.image?.width,
            imageHeight: s.image?.height,
            coordinates: s.coordinates,
          }
        }
      } catch {
        return { descriptors: [], unreadable: [] }
      }
      styleSnapshot = { at: performance.now(), layers, sources }
      return describeRasterLayers(layers, sources, mapZoom)
    },

    readImage(sourceId, rect) {
      try {
        let sample = imageSamples.get(sourceId)
        const live = (
          map.getSource(sourceId) as unknown as { image?: CanvasImageSource }
        )?.image
        if (!live) return null
        // Re-read when mapbox swaps the bitmap, which it does when the source's url changes.
        if (sample === undefined || (sample && sample.source !== live)) {
          const w = (live as { width?: number }).width ?? 0
          const h = (live as { height?: number }).height ?? 0
          sample = w && h ? readBackImage(live, w, h) : null
          imageSamples.set(sourceId, sample)
        }
        if (!sample) return null
        // The rect arrives in the bitmap's own pixels; the cache may hold a smaller copy.
        const scaled = scaleRectToDecodedSize(
          rect,
          sample.width / sample.scale,
          sample.width,
        )
        const clamped = {
          x: Math.min(Math.max(0, scaled.x), Math.max(0, sample.width - 1)),
          y: Math.min(Math.max(0, scaled.y), Math.max(0, sample.height - 1)),
          width: Math.max(1, Math.min(scaled.width, sample.width)),
          height: Math.max(1, Math.min(scaled.height, sample.height)),
        }
        clamped.width = Math.min(clamped.width, sample.width - clamped.x)
        clamped.height = Math.min(clamped.height, sample.height - clamped.y)
        return averageRgbaRect(sample.data, sample.width, clamped)
      } catch {
        return null
      }
    },

    async readTilePatches(url, rects, tileSize) {
      await acquire()
      let bitmap: ImageBitmap | null = null
      // A slot is held for the whole fetch, and release only runs once it settles, so four
      // requests that never answer — a captive portal, a tile host that accepts and stalls —
      // would strand every later read behind a queue that can never drain, leaving every label
      // on the basemap guess with nothing retrying. The sibling logo loader grew a timeout for
      // exactly this; this path had none.
      const abort = new AbortController()
      const timeout = window.setTimeout(
        () => abort.abort(),
        TILE_READ_TIMEOUT_MS,
      )
      try {
        const response = await fetch(url, {
          mode: 'cors',
          signal: abort.signal,
        })
        // Outside an overlay's footprint this is the normal answer, not a fault.
        if (!response.ok) return null
        // The one expensive step, now paid once however many rects come off this tile.
        bitmap = await createImageBitmap(await response.blob())
        const decoded = bitmap
        return rects.map(rect =>
          sampleFrom(
            decoded,
            scaleRectToDecodedSize(rect, tileSize, decoded.width),
          ),
        )
      } finally {
        window.clearTimeout(timeout)
        bitmap?.close()
        release()
      }
    },

    normaliseTileUrl(template, z, x, y, tileSize) {
      const rm = (
        map as unknown as {
          _requestManager?: {
            normalizeTileURL?: (
              url: string,
              use2x?: boolean,
              tileSize?: number,
            ) => string
          }
        }
      )._requestManager
      const filled = template
        .replace(/{z}/g, String(z))
        .replace(/{x}/g, String(x))
        .replace(/{y}/g, String(y))
      // No normaliser means an undocumented internal moved. A plain third-party tile URL is
      // still fetchable as-is, so fall back to it rather than going blind.
      if (typeof rm?.normalizeTileURL !== 'function') return filled || null
      try {
        // Appends the access token and picks the encoding mapbox itself requested, which is
        // what keeps our fetch on the same HTTP cache entry it already populated.
        return rm.normalizeTileURL(
          filled,
          (globalThis.devicePixelRatio ?? 1) >= 2,
          tileSize,
        )
      } catch {
        return filled || null
      }
    },

    report,
  }
}
